import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { cancellaSessione, leggiToken, segnaSessioneScaduta, sostituisciToken } from './sessione';

// Indirizzo del backend da frontend/.env. Vuoto = stesso dominio del sito (/api),
// come in produzione e in locale con il proxy di sviluppo verso il backend.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = `${BACKEND_URL}/api`;

// Sessione scaduta o non più valida (scaduta dopo 24 ore, email cambiata dallo staff, account eliminato): alla prima
// risposta 401 di una chiamata fatta col login si esce e si torna al login, che lo dice. Mai pagine vuote come se
// non ci fosse niente. Le chiamate dell'accesso (login, codice, passkey, reset) rispondono 401 per altri motivi.
const CHIAMATE_DI_ACCESSO = /\/auth\/(login|codice|passkey\/accedi|password-reset|esci)|\/clienti\/registrazione|\/condivisi\//;
const esciPerSessioneScaduta = () => {
  cancellaSessione();
  delete axios.defaults.headers.common.Authorization;
  segnaSessioneScaduta();
  // dopo il nuovo accesso si torna alla pagina su cui si era (il login controlla che sia una pagina del proprio ruolo)
  const { pathname, search, hash } = window.location;
  if (pathname !== '/login') window.location.replace(`/login?torna=${encodeURIComponent(pathname + (search || '') + (hash || ''))}`);
};
// Dopo il cambio password il server dà un token nuovo (i vecchi non valgono più): si continua con quello
const usaNuovoToken = (token) => {
  if (!token) return;
  sostituisciToken(token);
  axios.defaults.headers.common.Authorization = `Bearer ${token}`;
};

// Il token che quella chiamata aveva addosso (axios 1.x: le intestazioni sono un AxiosHeaders, che si legge con
// .get; con un oggetto normale, come nei test, si legge com'è scritta)
const tokenDellaChiamata = (config) => {
  const h = config?.headers;
  if (!h) return '';
  const valore = typeof h.get === 'function' ? h.get('Authorization') : (h.Authorization || h.authorization);
  return String(valore || '').replace(/^Bearer\s+/i, '');
};
// Di chi è un token: email e ruolo scritti dentro. Serve per non ripetere una chiamata col token di un altro.
const chiHaIlToken = (token) => {
  try {
    const d = jwtDecode(token);
    return `${d?.user_type || ''}:${d?.sub || ''}`;
  } catch { return null; }
};

axios.interceptors.response.use(undefined, (errore) => {
  const url = errore?.config?.url || '';
  const token = leggiToken();
  if (errore?.response?.status === 401 && url.includes('/api/') && !CHIAMATE_DI_ACCESSO.test(url) && token) {
    // Il token può essere cambiato mentre la chiamata era per strada: dopo il cambio password il server chiude le
    // sessioni di prima e ne dà uno nuovo (anche in un'altra scheda dello stesso cliente). Quel 401 riguarda il token
    // vecchio, non la sessione di adesso: se il token nuovo è della stessa persona si rifà la chiamata con quello,
    // una volta sola. Prima si usciva lo stesso e il cliente finiva sul login con «La sessione è scaduta: entrate di
    // nuovo». Se invece nel browser è entrato qualcun altro, la chiamata non si ripete (non si mostrano i suoi dati).
    const usato = tokenDellaChiamata(errore.config);
    const chi = usato && chiHaIlToken(usato);
    if (usato && usato !== token && !errore.config.__ripetuta && chi && chi === chiHaIlToken(token)) {
      const config = { ...errore.config, __ripetuta: true };
      config.headers = { ...(errore.config.headers?.toJSON?.() || errore.config.headers), Authorization: `Bearer ${token}` };
      return axios(config);
    }
    esciPerSessioneScaduta();
  }
  return Promise.reject(errore);
});

// Formattazione prezzo in stile italiano: €300.000,00
const formatPrezzo = (prezzo) => {
  if (!prezzo) return 'N/D';
  const numStr = String(prezzo).replace(/[^\d.,]/g, '').replace(',', '.');
  const num = parseFloat(numStr);
  if (isNaN(num)) return prezzo;
  return '€' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.').replace(/\.(\d{2})$/, ',$1');
};

// Garantisce che un URL sia assoluto (con protocollo http/https)
const ensureAbsoluteUrl = (url) => {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return 'https://' + url;
};

// Converte una Date in stringa ISO locale (senza conversione UTC)
const toLocalISO = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};

// Formatta data in italiano: GG/MM/AAAA HH:mm (24h)
const formatDataIT = (dateStr, options = {}) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const { showDate = true, showTime = true, showYear = true, showWeekday = false } = options;
  const pad = (n) => String(n).padStart(2, '0');
  const giorni = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
  let result = '';
  if (showWeekday) result += giorni[d.getDay()] + ' ';
  if (showDate) {
    result += `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
    if (showYear) result += `/${d.getFullYear()}`;
  }
  if (showTime) {
    if (showDate) result += ' ';
    result += `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return result.trim();
};

// Formato breve: "18 apr 15:00"
const formatDataShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const mesiShort = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
  return `${pad(d.getDate())} ${mesiShort[d.getMonth()]} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Formato giorno breve: "ven 18/04"
const formatGiornoShort = (date) => {
  const giorniShort = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];
  const pad = (n) => String(n).padStart(2, '0');
  return `${giorniShort[date.getDay()]} ${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
};

// Solo data: "18/04/2026"
const formatSoloData = (dateStr) => formatDataIT(dateStr, { showTime: false });

// Solo ora: "15:00"
const formatOra = (dateStr) => formatDataIT(dateStr, { showDate: false });

// Scarica un documento usando il login: il backend lo consegna solo come allegato.
// (Aprirlo in una nuova scheda non funzionerebbe: la scheda non invia il token.)
const scaricaDocumento = async (doc) => {
  const res = await axios.get(`${API}/documenti/${doc.id}/download`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.nome_file || 'documento';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export { BACKEND_URL, API, scaricaDocumento, formatPrezzo, ensureAbsoluteUrl, toLocalISO, formatDataIT, formatDataShort, formatGiornoShort, formatSoloData, formatOra, axios, usaNuovoToken };
